/**
 * Security Section Component
 * Manages 2FA and Passkey settings
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import TwoFactorSetup from './TwoFactorSetup';
import PasskeyManager from './PasskeyManager';

const Section = styled.div`
  display: grid;
  gap: 2rem;
`;

const Card = styled.div`
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  color: var(--accent-primary);
  font-size: 1.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Description = styled.p`
  color: var(--text-primary);
  opacity: 0.7;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const SecurityOverview = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 2rem;
  color: white;
  margin-bottom: 2rem;
`;

const OverviewTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SecurityScore = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ScoreCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.$bgColor || 'rgba(255, 255, 255, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  font-weight: bold;
`;

const ScoreInfo = styled.div`
  flex: 1;
`;

const ScoreLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 0.25rem;
`;

const RecommendationsTitle = styled(ScoreLabel)`
  margin-bottom: 0.75rem;
  margin-top: 1rem;
`;

const ScoreText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
`;

const Recommendations = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const Recommendation = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 0.95rem;
`;

const RecommendationIcon = styled.span`
  font-size: 1.25rem;
`;

function SecuritySection({ user }) {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user.twoFactorEnabled || false);
  const [passkeysCount, setPasskeysCount] = useState(user.passkeys?.length || 0);

  // Calculate security score
  const calculateSecurityScore = () => {
    let score = 0;
    if (user.password) score += 25;
    if (user.googleId) score += 25;
    if (twoFactorEnabled) score += 30;
    if (passkeysCount > 0) score += 20;
    return score;
  };

  const securityScore = calculateSecurityScore();
  const scoreColor = securityScore >= 75 ? '#4CAF50' : securityScore >= 50 ? '#FF9800' : '#F44336';

  const getRecommendations = () => {
    const recommendations = [];
    if (!twoFactorEnabled) {
      recommendations.push({
        icon: '🔐',
        text: 'Enable Two-Factor Authentication for enhanced security'
      });
    }
    if (passkeysCount === 0) {
      recommendations.push({
        icon: '🔑',
        text: 'Add a passkey for passwordless login'
      });
    }
    if (!user.googleId) {
      recommendations.push({
        icon: '🔗',
        text: 'Link your Google account for easier recovery'
      });
    }
    return recommendations;
  };

  const recommendations = getRecommendations();

  return (
    <>
      <SecurityOverview>
        <OverviewTitle>
          <span>🛡️</span>
          Security Overview
        </OverviewTitle>

        <SecurityScore>
          <ScoreCircle $bgColor={scoreColor}>
            {securityScore}%
          </ScoreCircle>
          <ScoreInfo>
            <ScoreLabel>Security Score</ScoreLabel>
            <ScoreText>
              {securityScore >= 75 ? 'Excellent' : securityScore >= 50 ? 'Good' : 'Needs Improvement'}
            </ScoreText>
          </ScoreInfo>
        </SecurityScore>

        {recommendations.length > 0 && (
          <>
            <RecommendationsTitle>
              Recommendations:
            </RecommendationsTitle>
            <Recommendations>
              {recommendations.map((rec, index) => (
                <Recommendation key={index}>
                  <RecommendationIcon>{rec.icon}</RecommendationIcon>
                  {rec.text}
                </Recommendation>
              ))}
            </Recommendations>
          </>
        )}
      </SecurityOverview>

      <Section>
        <Card>
          <SectionTitle>
            <span>🔐</span>
            Two-Factor Authentication
          </SectionTitle>
          <Description>
            Add an extra layer of security to your account by requiring a verification code
            from your authenticator app in addition to your password when signing in.
          </Description>
          <TwoFactorSetup
            enabled={twoFactorEnabled}
            onStatusChange={setTwoFactorEnabled}
          />
        </Card>

        <Card>
          <SectionTitle>
            <span>🔑</span>
            Passkeys (WebAuthn)
          </SectionTitle>
          <Description>
            Passkeys provide a secure, passwordless way to sign in using your device's biometrics
            (Face ID, Touch ID, Windows Hello) or security keys. They're more secure than
            passwords and can't be phished.
          </Description>
          <PasskeyManager
            passkeys={user.passkeys || []}
            onPasskeysChange={(newCount) => setPasskeysCount(newCount)}
          />
        </Card>
      </Section>
    </>
  );
}

export default SecuritySection;
